import VideoResource from 'types/VideoResource';

// URL query-param keys; kept in the URL so filters survive refresh / deep-links.
export const TAGS_PARAM = 'tags';
export const TINY_PLUS_PARAM = 'tiny_plus_only';

// We surface only these pills, not the DB's full tag set. The "Tiny+ Exclusive"
// pill toggles the `is_tiny_plus_only` flag instead of matching a tag string.
export const CURATED_TAG_FILTERS = ['Baby', 'Adult', 'Vaginal'] as const;

export const TINY_PLUS_TAG_LABEL = 'Tiny+ Exclusive';

// Both the new label and the legacy DB value get the gold treatment during rollout.
export const TINY_PLUS_TAG_VARIANTS = new Set<string>([
  TINY_PLUS_TAG_LABEL,
  'Tiny+ Member Exclusive',
]);

export const UPSELL_SOURCE = 'resources_page' as const;

// Appended to the checkout URL to attribute conversions from this modal.
export const UPSELL_CHECKOUT_SOURCE = 'resources_upsell';

// Copy verbatim from Figma (node 2163:13020).
export const UPSELL_BENEFITS = [
  '2 gut or vaginal health tests annually',
  'A complementary 1-on-1 coaching session with a microbiome specialist annually',
  'Order additional tests for your whole family at a members-only rate of $169/kit',
  'Additional coaching sessions at $39/call',
  'Exclusive access to members-only virtual events and resources',
  'Unlimited text and email support throughout the year',
];

export interface ResourceFilters {
  tags: string[];
  tinyPlusOnly: boolean;
}

// Pulls the 11-char id from the various YouTube URL shapes (watch?v=, youtu.be/,
// /embed/, /v/).
const YOUTUBE_ID_REGEX =
  /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;

export const getYouTubeVideoId = (videoUrl?: string): string => {
  if (!videoUrl) return '';
  const match = videoUrl.match(YOUTUBE_ID_REGEX);
  return match ? match[1] : '';
};

export const getYouTubeThumbnail = (videoUrl?: string): string => {
  const videoId = getYouTubeVideoId(videoUrl);
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';
};

// Tags are stored as a single comma-separated string on the resource row.
export const parseTags = (raw?: string): string[] =>
  (raw ?? '')
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);

// Shared by the visible-list memo and the analytics result-count so the two
// can't drift.
export const matchesFilters = (
  video: VideoResource,
  { tags, tinyPlusOnly }: ResourceFilters,
): boolean => {
  if (tinyPlusOnly && !video.is_tiny_plus_only) return false;
  if (tags.length) {
    const videoTags = parseTags(video.tags);
    if (!tags.some(tag => videoTags.includes(tag))) return false;
  }
  return true;
};

// Locked for the current viewer. Key off `is_tiny_plus_only`, never `video_url`:
// exclusive videos still carry a URL server-side for member playback.
export const isExclusiveLocked = (
  video: VideoResource,
  isAnyTinyPlus: boolean,
): boolean => !!video.is_tiny_plus_only && !isAnyTinyPlus;

interface GateArgs {
  source?: string;
  hideUpsells?: boolean;
  isAnyTinyPlus: boolean;
}

// Member/partner gating applied before the filter pills. Only a true cobranded
// partner strips exclusive content; elsewhere we keep it and let the modal gate
// playback. Members always see everything.
export const gateVideoResources = (
  videos: VideoResource[] | undefined,
  { source, hideUpsells, isAnyTinyPlus }: GateArgs,
): VideoResource[] | undefined => {
  if (!videos) return undefined;
  if (source === 'cobranded_partner' && hideUpsells && !isAnyTinyPlus) {
    return videos.filter(video => !video.is_tiny_plus_only);
  }
  return videos;
};
